import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSFormField, ILMSInput, ILMSSelect } from '../../components/ILMSForm';
import { getApiErrorMessage } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { BarChart3, Images, Megaphone, Paintbrush, SlidersHorizontal } from 'lucide-react';
import { APPEARANCE_DEFAULTS, applyFontScale, getAppearance, saveAppearance, type Appearance } from '../../utils/appearance';
import { cn } from '../../utils/cn';
import { ThemePanel } from './appearance/ThemePanel';
import { WebsitePanel } from './appearance/WebsitePanel';
import { PhotosPanel } from './appearance/PhotosPanel';
import { ChartsPanel } from './appearance/ChartsPanel';

const FONT_SIZES = [
  { value: 90, label: 'Small' },
  { value: 100, label: 'Normal' },
  { value: 112, label: 'Large' },
  { value: 125, label: 'Extra large' },
];
const SAMPLE_DAY = new Date(2026, 8, 14);

const SECTIONS = [
  { id: 'general', label: 'General', icon: SlidersHorizontal },
  { id: 'theme', label: 'Theme & Logo', icon: Paintbrush },
  { id: 'website', label: 'Website & News', icon: Megaphone },
  { id: 'photos', label: 'Photos & Gallery', icon: Images },
  { id: 'charts', label: 'Charts', icon: BarChart3 },
] as const;
type SectionId = (typeof SECTIONS)[number]['id'];
const SECTION_KEY = 'ilms.appearanceSection';

// Everything about how the web app and website look - changed here by an
// Admin, no developer needed. Saved on the server; every user's browser
// picks it up when it next opens (or reloads) the app.
export function AppearanceTab() {
  const [section, setSection] = useState<SectionId>(() => {
    try {
      const v = localStorage.getItem(SECTION_KEY) as SectionId | null;
      return v && SECTIONS.some((s) => s.id === v) ? v : 'general';
    } catch {
      return 'general';
    }
  });
  const choose = (id: SectionId) => {
    setSection(id);
    try { localStorage.setItem(SECTION_KEY, id); } catch { /* not remembered */ }
  };
  return (
    <ILMSCard>
      <div className="flex flex-col gap-1 mb-3">
        <h2 className="text-card-title text-ink-900">Appearance</h2>
        <p className="text-secondary text-ink-500">How the web app, the website and the charts look for everyone.</p>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-5 border-b border-ink-300/15 pb-3" role="tablist">
        {SECTIONS.map((s) => (
          <button
            key={s.id} type="button" role="tab" aria-selected={section === s.id} onClick={() => choose(s.id)}
            className={cn('inline-flex items-center gap-1.5 rounded-md px-3 h-9 text-secondary font-medium',
              section === s.id ? 'bg-brand-green-700 text-white' : 'text-ink-600 hover:bg-surface-muted')}
          >
            <s.icon className="h-4 w-4" /> {s.label}
          </button>
        ))}
      </div>
      {section === 'general' && <GeneralPanel />}
      {section === 'theme' && <ThemePanel />}
      {section === 'website' && <WebsitePanel />}
      {section === 'photos' && <PhotosPanel />}
      {section === 'charts' && <ChartsPanel />}
    </ILMSCard>
  );
}

function GeneralPanel() {
  const { can } = useAuth();
  const canEdit = can('System Settings', 'Edit');
  const [value, setValue] = useState<Appearance>(getAppearance());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof Appearance>(k: K, v: Appearance[K]) => setValue((cur) => ({ ...cur, [k]: v }));

  // Preview the text size live; put the saved size back on leaving without saving.
  useEffect(() => { applyFontScale(value.font_scale); }, [value.font_scale]);
  useEffect(() => () => applyFontScale(), []);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveAppearance(value);
      window.location.reload(); // apply everywhere, including labels set at load
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not save the appearance.'));
      setSaving(false);
    }
  };

  return (
    <div>
      {error && <ILMSAlert tone="critical">{error}</ILMSAlert>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
        <ILMSFormField label="Text size" htmlFor="font_scale" helperText="Makes every page's text and spacing bigger or smaller.">
          <ILMSSelect id="font_scale" disabled={!canEdit} value={value.font_scale} onChange={(e) => set('font_scale', Number(e.target.value))}>
            {FONT_SIZES.map((f) => <option key={f.value} value={f.value}>{f.label} ({f.value}%)</option>)}
          </ILMSSelect>
        </ILMSFormField>
        <ILMSFormField label="Kannada labels" htmlFor="show_kannada" helperText={'e.g. "Milk Sessions / ಹಾಲು ಅಧಿವೇಶನಗಳು" on the Dairy page.'}>
          <ILMSSelect id="show_kannada" disabled={!canEdit} value={value.show_kannada ? 'yes' : 'no'} onChange={(e) => set('show_kannada', e.target.value === 'yes')}>
            <option value="yes">Show (English / Kannada)</option>
            <option value="no">English only</option>
          </ILMSSelect>
        </ILMSFormField>
        <ILMSFormField label="Date style" htmlFor="date_format">
          <ILMSSelect id="date_format" disabled={!canEdit} value={value.date_format} onChange={(e) => set('date_format', e.target.value as Appearance['date_format'])}>
            {(['dd MMM yyyy', 'dd/MM/yyyy', 'yyyy-MM-dd'] as const).map((f) => <option key={f} value={f}>{format(SAMPLE_DAY, f)}</option>)}
          </ILMSSelect>
        </ILMSFormField>
        <ILMSFormField label="Rows per page (to start with)" htmlFor="page_size" helperText="Each table can still be changed with its own Rows list.">
          <ILMSSelect id="page_size" disabled={!canEdit} value={value.page_size} onChange={(e) => set('page_size', Number(e.target.value) as Appearance['page_size'])}>
            {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </ILMSSelect>
        </ILMSFormField>
        <ILMSFormField label="Left menu when the app opens" htmlFor="sidebar_default" helperText="Anyone who has shown/hidden it themselves keeps their own choice.">
          <ILMSSelect id="sidebar_default" disabled={!canEdit} value={value.sidebar_default} onChange={(e) => set('sidebar_default', e.target.value as Appearance['sidebar_default'])}>
            <option value="shown">Shown</option>
            <option value="hidden">Hidden (more room for pages)</option>
          </ILMSSelect>
        </ILMSFormField>
        <ILMSFormField label="Table rows" htmlFor="table_density" helperText="Compact fits about twice as many rows on the screen.">
          <ILMSSelect id="table_density" disabled={!canEdit} value={value.table_density} onChange={(e) => set('table_density', e.target.value as Appearance['table_density'])}>
            <option value="compact">Compact (more rows on screen)</option>
            <option value="comfortable">Comfortable (taller rows)</option>
          </ILMSSelect>
        </ILMSFormField>
        <ILMSFormField label="Name in the header" htmlFor="header_title">
          <ILMSInput id="header_title" disabled={!canEdit} maxLength={60} value={value.header_title} onChange={(e) => set('header_title', e.target.value)} />
        </ILMSFormField>
        <ILMSFormField label="Second line in the header" htmlFor="header_subtitle" helperText="Leave blank for none.">
          <ILMSInput id="header_subtitle" disabled={!canEdit} maxLength={60} value={value.header_subtitle} onChange={(e) => set('header_subtitle', e.target.value)} />
        </ILMSFormField>
      </div>

      <div className="mt-5 rounded-md border border-ink-300/20 bg-surface-subtle p-4 max-w-3xl">
        <p className="text-helper-text text-ink-400 uppercase tracking-wide mb-1">Preview</p>
        <p className="text-card-title text-ink-900">{value.header_title || '—'}</p>
        <p className="text-body text-ink-700">Milk Sessions{value.show_kannada ? ' / ಹಾಲು ಅಧಿವೇಶನಗಳು' : ''} · {format(SAMPLE_DAY, value.date_format)} · 55.1 L</p>
      </div>

      {canEdit ? (
        <div className="flex gap-2 mt-5">
          <ILMSButton onClick={save} loading={saving} disabled={!value.header_title.trim()}>Save appearance</ILMSButton>
          <ILMSButton variant="ghost" onClick={() => setValue(APPEARANCE_DEFAULTS)}>Back to defaults</ILMSButton>
        </div>
      ) : (
        <p className="mt-4 text-helper-text text-ink-500">Only users who can edit Settings can change these.</p>
      )}
    </div>
  );
}
